import React, { useState, useEffect } from 'react';
import axios from 'axios';

const api = () => axios.create({
 baseURL: '/api',
 headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const WORKER_STEPS = [
 {
 id: 'welcome',
 title: 'Welcome to ShiftMatch! ',
 body: 'You\'re about to find the easiest way to earn money in Australia. Let\'s show you around — this takes 30 seconds.',
 icon: '',
 position: 'center',
 },
 {
 id: 'start-earning',
 title: ' Start Earning',
 body: 'This tab shows shifts anyone can do — no experience, no CV, no qualifications. Just apply and show up. Perfect if you\'re new.',
 icon: '',
 highlight: 'Start Earning',
 position: 'top',
 },
 {
 id: 'skilled-work',
 title: '⭐ Skilled Work',
 body: 'Got certifications? These shifts pay more — up to $55/hr. Upload your RSA, White Card, or other certs to unlock them.',
 icon: '⭐',
 highlight: 'Skilled Work',
 position: 'top',
 },
 {
 id: 'apply',
 title: 'Swipe right to apply ➡️',
 body: 'See a shift you like? Swipe right to instantly apply — or tap "Quick Apply". Swipe left on ones you\'re not interested in and we\'ll show you less like that.',
 icon: '',
 position: 'center',
 },
 {
 id: 'profile',
 title: 'Complete your profile ',
 body: 'Add a photo, phone number, and bank details. A complete profile gets you hired 3× faster. Employers trust faces, not blank avatars.',
 icon: '',
 position: 'center',
 },
 {
 id: 'done',
 title: 'You\'re all set! ',
 body: 'Start browsing shifts now. New ones post every day. The earlier you apply, the more likely you get hired. Go earn.',
 icon: '',
 position: 'center',
 },
];

const EMPLOYER_STEPS = [
 {
 id: 'welcome',
 title: 'Welcome to ShiftMatch! ',
 body: 'Post a shift, get applicants in minutes, hire verified workers. Let\'s show you around.',
 icon: '',
 position: 'center',
 },
 {
 id: 'post',
 title: 'Post a Shift ➕',
 body: 'Fill in the details — title, pay, location, hours. The more specific, the better applicants you\'ll get. Entry-level shifts fill fastest.',
 icon: '➕',
 position: 'top',
 },
 {
 id: 'applicants',
 title: 'Review Applicants ',
 body: 'Workers apply instantly. Review their profile, certs, and ratings. Hire with one tap. Message them directly.',
 icon: '',
 position: 'top',
 },
 {
 id: 'analytics',
 title: 'Track Everything ',
 body: 'See how your shifts perform — views, applications, hire rate. We\'ll give you tips to improve.',
 icon: '',
 position: 'top',
 },
 {
 id: 'done',
 title: 'Ready to hire! ',
 body: 'Post your first shift now. It\'s free — you only pay 15% when a worker actually completes the shift. Fair and transparent.',
 icon: '',
 position: 'center',
 },
];

const ADMIN_STEPS = [
 {
   id: 'welcome',
   title: 'Admin Dashboard',
   body: 'Welcome to the ShiftMatch control centre. Here you can monitor platform health, manage users, review shifts, and handle reports.',
   icon: '🎛️',
   position: 'center',
 },
 {
   id: 'overview',
   title: 'Platform Overview',
   body: 'Your main dashboard shows real-time stats — workers, employers, shifts, applications, and completion rates. The health score tells you how the platform is performing.',
   icon: '📊',
   position: 'center',
 },
 {
   id: 'users',
   title: 'User Management',
   body: 'Track worker and employer growth. Monitor sign-ups against Q2 targets. Identify inactive users who need re-engagement.',
   icon: '👥',
   position: 'center',
 },
 {
   id: 'reports',
   title: 'Reports & Safety',
   body: 'Review user reports, manage strikes, and keep the platform safe. Critical reports are flagged automatically.',
   icon: '🛡️',
   position: 'center',
 },
 {
   id: 'done',
   title: 'You\'re in control',
   body: 'Use the tabs to dive into any section. Metrics gives you the full financial picture. Let\'s build something great.',
   icon: '🚀',
   position: 'center',
 },
];

export default function OnboardingTour({ role = 'worker', onComplete }) {
 const [step, setStep] = useState(0);
 const [show, setShow] = useState(false);
 const http = api();

 const steps = role === 'admin' ? ADMIN_STEPS : role === 'employer' ? EMPLOYER_STEPS : WORKER_STEPS;

 useEffect(() => {
 const checkOnboarding = async () => {
 try {
 const res = await http.get('/account/onboarding');
 if (!res.data.complete) setShow(true);
 } catch {
 // If endpoint fails, check localStorage fallback
 if (!localStorage.getItem('onboarding_done')) setShow(true);
 }
 };
 checkOnboarding();
 }, []);

 const handleNext = () => {
 if (step < steps.length - 1) {
 setStep(step + 1);
 } else {
 handleDone();
 }
 };

 const handleDone = async () => {
 setShow(false);
 localStorage.setItem('onboarding_done', 'true');
 try { await http.post('/account/onboarding/complete'); } catch { /* silent */ }
 if (onComplete) onComplete();
 };

 const handleSkip = () => {
 handleDone();
 };

 if (!show) return null;

 const current = steps[step];
 const isLast = step === steps.length - 1;
 const progress = ((step + 1) / steps.length) * 100;

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center">
 {/* Backdrop */}
 <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleSkip} />

 {/* Modal */}
 <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden animate-in">
 {/* Progress bar */}
 <div className="h-1 bg-neutral-100">
 <div className="h-1 bg-brand-500 transition-all duration-300" style={{ width: `${progress}%` }} />
 </div>

 <div className="p-6 text-center">
 {/* Icon */}
 <div className="text-5xl mb-4">{current.icon}</div>

 {/* Content */}
 <h2 className="text-lg font-bold text-neutral-900 mb-2">{current.title}</h2>
 <p className="text-sm text-neutral-500 leading-relaxed mb-6">{current.body}</p>

 {/* Step indicator */}
 <div className="flex justify-center gap-1.5 mb-5">
 {steps.map((_, i) => (
 <div key={i} className={`w-2 h-2 rounded-full transition-all ${
 i === step ? 'bg-brand-500 w-6' : i < step ? 'bg-brand-300' : 'bg-neutral-200'
 }`} />
 ))}
 </div>

 {/* Actions */}
 <div className="flex gap-3">
 {!isLast && (
 <button onClick={handleSkip}
 className="flex-1 py-2.5 text-xs text-neutral-400 font-medium rounded-xl hover:bg-neutral-50 transition">
 Skip tour
 </button>
 )}
 <button onClick={handleNext}
 className={`flex-1 py-2.5 font-bold text-sm rounded-xl transition ${
 isLast
 ? 'bg-brand-500 hover:bg-brand-600 text-white'
 : 'bg-brand-500 hover:bg-brand-600 text-white'
 }`}>
 {isLast ? 'Let\'s go! ' : `Next (${step + 1}/${steps.length})`}
 </button>
 </div>
 </div>
 </div>
 </div>
 );
}
